import { useEffect, useState } from "react";
import { Save, Loader2, HandCoins, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminFetchMonetization, adminSaveMonetization, formatApiError } from "@/lib/auth";

const inputCls = "w-full bg-ink border border-white/10 focus:border-gold/60 outline-none px-3 py-2.5 text-sm placeholder:text-slate-600 transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5";

const ItemEditor = ({ item, onChange, onRemove, fields, testIdPrefix }) => (
    <div className="border border-white/10 bg-ink p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        {fields.map((f) => (
            <div key={f.key} className={f.span}>
                <label className={labelCls}>{f.label}</label>
                <input
                    data-testid={`${testIdPrefix}-${f.key}`}
                    value={item[f.key] || ""}
                    onChange={(e) => onChange({ ...item, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className={inputCls}
                />
            </div>
        ))}
        <div className="md:col-span-1 flex justify-end">
            <button type="button" onClick={onRemove} data-testid={`${testIdPrefix}-remove`} className="p-2.5 border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-400/50 transition-colors" title="Remover">
                <Trash2 size={14} />
            </button>
        </div>
    </div>
);

const ListEditor = ({ title, items, setItems, fields, newItem, testIdPrefix }) => (
    <div>
        <h3 className="font-serif text-xl tracking-tight mb-4">{title}</h3>
        <div className="flex flex-col gap-3">
            {items.map((item, i) => (
                <ItemEditor
                    key={i}
                    item={item}
                    fields={fields}
                    testIdPrefix={`${testIdPrefix}-${i}`}
                    onChange={(updated) => setItems(items.map((x, j) => (j === i ? updated : x)))}
                    onRemove={() => setItems(items.filter((_, j) => j !== i))}
                />
            ))}
        </div>
        <button
            type="button"
            data-testid={`${testIdPrefix}-add`}
            onClick={() => setItems([...items, { ...newItem }])}
            className="mt-3 flex items-center gap-2 border border-dashed border-white/20 text-slate-400 px-4 py-2.5 text-xs uppercase tracking-[0.15em] hover:border-gold hover:text-gold transition-colors"
        >
            <Plus size={13} /> Adicionar item
        </button>
    </div>
);

const SPONSORED_FIELDS = [
    { key: "name", label: "Nome do produto", span: "md:col-span-3", placeholder: "Cartão Gold" },
    { key: "url", label: "URL (seu link real)", span: "md:col-span-4", placeholder: "https://..." },
    { key: "cta", label: "Botão", span: "md:col-span-2", placeholder: "Solicitar" },
    { key: "description", label: "Descrição", span: "md:col-span-2", placeholder: "Uma linha" },
];

const AFFILIATE_FIELDS = [
    { key: "name", label: "Nome da ferramenta", span: "md:col-span-3", placeholder: "Curso X" },
    { key: "url", label: "URL (seu link de afiliado)", span: "md:col-span-4", placeholder: "https://..." },
    { key: "tag", label: "Selo", span: "md:col-span-2", placeholder: "Recomendado" },
    { key: "description", label: "Descrição", span: "md:col-span-2", placeholder: "Uma linha" },
];

export const AdminMonetization = () => {
    const [sponsored, setSponsored] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminFetchMonetization()
            .then((d) => {
                setSponsored(d.sponsored_products || []);
                setAffiliates(d.affiliate_tools || []);
                setLoaded(true);
            })
            .catch(() => toast.error("Não foi possível carregar a configuração de monetização."));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await adminSaveMonetization({ sponsored_products: sponsored, affiliate_tools: affiliates });
            toast.success("Monetização atualizada — já está no ar no site.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return <Loader2 size={20} className="animate-spin text-gold" />;

    return (
        <div className="mt-14">
            <h2 className="font-serif text-2xl tracking-tight mb-2 flex items-center gap-3">
                <HandCoins size={18} className="text-gold" /> Monetização
            </h2>
            <p className="text-xs text-slate-500 mb-6 max-w-lg leading-relaxed">
                Edite os links de afiliado e produtos patrocinados exibidos no site. Cole suas URLs reais aqui —
                as mudanças entram no ar imediatamente após salvar.
            </p>
            <div className="border border-white/10 bg-paper p-6 flex flex-col gap-10">
                <ListEditor
                    title="Produtos patrocinados"
                    items={sponsored}
                    setItems={setSponsored}
                    fields={SPONSORED_FIELDS}
                    newItem={{ name: "", url: "", description: "", cta: "Saiba mais", partner: "" }}
                    testIdPrefix="sponsored-item"
                />
                <ListEditor
                    title="Ferramentas afiliadas"
                    items={affiliates}
                    setItems={setAffiliates}
                    fields={AFFILIATE_FIELDS}
                    newItem={{ name: "", url: "", description: "", tag: "Recomendado" }}
                    testIdPrefix="affiliate-item"
                />
                <button
                    data-testid="monetization-save-btn"
                    onClick={save}
                    disabled={saving}
                    className="self-start flex items-center gap-2 bg-gold text-ink font-medium px-6 py-3 text-sm hover:bg-golddark transition-colors disabled:opacity-50"
                >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    Salvar monetização
                </button>
            </div>
        </div>
    );
};
